import { Button, Divider, Form, message } from "antd";
import dayjs from "dayjs";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";

import "./WarrantyInfo.scss";
import WarrantyPrintButton from "./components/WarrantyPrintButton";
import { createDetailColumns } from "./content/warrantyColumns";
import { StatusTypes } from "./content/warrantyContent";
import MainContentHeader from "../../../components/layout/mainContent/MainContentHeader";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import NormalTag from "../../../components/tags/NormalTag";
import TableCustom from "../../../components/ui/TableCustom";
import { Colors } from "../../../constants/Colors";
import { useGetWarrantyInfoListQuery } from "../../../graphql/operations/clubApp/warranty/get-warranty-info";
import { useUpdateWarrantyStatusMutation } from "../../../graphql/operations/clubApp/warranty/update-warranty-status";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { __currentUser__ } from "../../../graphql/policies";
import { getSessionMarket } from "../../../i18n/featureLanguageSession";
import { NormalTagTypes, ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface WarrantyInfoProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const textStyle: React.CSSProperties = {
  color: Colors.notEditableText,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
  padding: '4px 0',
};

const WarrantyInfo = ({
  setSelectedService,
  setSelectSubItem,
}: WarrantyInfoProps) => {
  const currentUser = useReactiveVar(__currentUser__);
  const { applicationId }: any = useParams();
  const [form] = Form.useForm();
  const { t: trans } = useTranslation(["clubApp", "common"]);
  const sessionMarket = getSessionMarket();

  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 2,
      },
    });

  const { data, loading, refetch } = useGetWarrantyInfoListQuery({
    variables: { applicationIds: [Number(applicationId)] },
    skip: !applicationId
  });

  const warrantyInfo = data?.warrantyInfoList?.[0] ?? null;

  const [updateStatus] = useUpdateWarrantyStatusMutation();

  const statusTagMap: Record<StatusTypes, JSX.Element> = {
    [StatusTypes.Awaiting]: (
      <NormalTag type={NormalTagTypes.AWAITING} />
    ),
    [StatusTypes.Complete]: (
      <NormalTag type={NormalTagTypes.COMPLETE} />
    ),
    [StatusTypes.Canceled]: (
      <NormalTag type={NormalTagTypes.CANCELED} />
    ),
  };

  const handleUpdateStatusClick = async (type: StatusTypes) => {
    try {
      await updateStatus({
        variables: {
          applicationId: [Number(applicationId)],
          status: type,
          updateBy: currentUser?.userName!,
        }
      }).then(() => {
        if (type === StatusTypes.Complete)
          message.success(trans("clubApp:warranty.statusUpdatedToCompleteChecking"))
        else if (type === StatusTypes.Canceled)
          message.success(trans("clubApp:warranty.statusUpdatedToCanceled"))
      });


      await refetch();
    } catch (e) { }
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Warranty");
  }, []);

  if (userProgramsLoading || loading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Warranty"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="warrantyInfoWrapper">
      <div style={{ position: "relative", paddingBottom: 48 }}>
        <div className="main-header-div">
          <MainContentHeader
            title="Application info"
          />
          <div className="main-header-print-div">
            {warrantyInfo &&
              <WarrantyPrintButton
                applicationIds={[Number(applicationId)]}
                buttonText={trans("common:button.print")}
                type="primary"
                market={sessionMarket}
              />
            }
          </div>
        </div>
        <Form
          form={form}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 14 }}
        >
          <div className="content-container">
            <Form.Item label={trans("clubApp:warranty.applicationNo")}>
              <p style={textStyle}>{warrantyInfo?.applicationNo}</p>
            </Form.Item>
            <Form.Item label={trans("clubApp:warranty.applicationDate")}>
              <p style={textStyle}>{warrantyInfo?.applicationDate ? dayjs(warrantyInfo?.applicationDate).format('YYYY/MM/DD HH:mm') : ""}</p>
            </Form.Item>
            <Form.Item label={trans("clubApp:warranty.ACNO")}>
              <p style={textStyle}>{warrantyInfo?.accountNo}</p>
            </Form.Item>
            <Form.Item label={trans("clubApp:warranty.itemQty")}>
              <p style={textStyle}>{warrantyInfo?.itemQty}</p>
            </Form.Item>
            <Form.Item label={trans("common:common.status")}>
              {statusTagMap[warrantyInfo?.status as StatusTypes]}
            </Form.Item>
            {warrantyInfo?.status !== StatusTypes.Awaiting &&
              <Form.Item label={trans("clubApp:warranty.statusUpdatedDate")}>
                <p style={textStyle}>{warrantyInfo?.updateDate ? dayjs(warrantyInfo?.updateDate).format('YYYY/MM/DD') : ""}</p>
              </Form.Item>
            }
            {warrantyInfo?.status === StatusTypes.Awaiting &&
              <div>
                <Divider />
                <div className={"flex justify-between"}>
                  <Button
                    className="btn-cancel"
                    onClick={() => handleUpdateStatusClick(StatusTypes.Canceled)}
                  >
                    {trans("clubApp:warranty.cancelApplication")}
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => handleUpdateStatusClick(StatusTypes.Complete)}
                  >
                    {trans("clubApp:warranty.changeToCompleteChecking")}
                  </Button>

                </div>
              </div>
            }
          </div>
          <div className="theme-tab-content-container">
            <div
              className="theme-tab-table-container"
              style={{ backgroundColor: Colors.white }}
            >
              <div className="table-title-container">
                <p>{"絵本、ディスク、カードなど (" + (warrantyInfo?.otherProductList.length ?? 0) + ")"}</p>
                <Form.Item noStyle>
                  <TableCustom
                    className="custom-table-style"
                    dataSource={warrantyInfo?.otherProductList || []}
                    columns={createDetailColumns(trans)}
                    pagination={false}
                    rowKey="categoryId"
                  />
                </Form.Item>
              </div>
              <div className="table-title-container">
                <p>{"トークアロングカード (" + (warrantyInfo?.talkalongCardProductList.length ?? 0) + ")"}</p>
                <Form.Item noStyle>
                  <TableCustom
                    className="custom-table-style"
                    dataSource={warrantyInfo?.talkalongCardProductList || []}
                    columns={createDetailColumns(trans)}
                    pagination={false}
                    rowKey="categoryId"
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default WarrantyInfo;
